import { useCallback, useEffect, useState } from "react";
import {
  FunctionWithVoidReturn,
  WebApiPermissionStates,
} from "../utils/constants/generic-types";
import {
  checkGeoLocationPermission,
  getDeviceGeoLocation,
  removeTrailingSlash,
} from "../utils/helpers";
import useDisclosure from "./useDisclosure";
import { useLocation, useNavigate } from "react-router-dom";
import { ROUTES } from "../utils/constants";
import { handleGetMyTransactionHistory } from "../api-clients/walletsTransactionsClient";
import { storeLocationData } from "../api-clients/complianceGeoLocationClient";
import Logger from "../utils/logger";
import useAuth from "./useAuth";
import {
  HandleGetDeviceLocationInfo,
  HandleTopup,
} from "../components/topup/TopupFlow/types";
import useUserVerificationStatusQuery from "../react-query/queries/use-user-verification-status";
import { useAtom } from "jotai";
import { userHasAllowedLocationAtom } from "../jotai/atoms/user";

const TOPUP_PAGE_ROUTE = `${ROUTES.WALLET.TOPUP}`;
const WALLET_PAGE_ROUTE = `${ROUTES.WALLET.ROOT}`;

export default function useTopupFlow(params?: { isCustomFlow?: boolean }) {
  const { isCustomFlow } = params ?? {};
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoggedIn, userProfile } = useAuth();
  const {
    isLegalAge,
    checkIsLegalAge,
    userVerificationData,
    userVerificationIsLoading,
  } = useUserVerificationStatusQuery();
  const [isFirstTopup, setIsFirstTopup] = useState<boolean>();
  const [ipLocationCountry, setIpLocationCountry] = useState<string>();
  const [userHasAllowedLocation, setUserHasAllowedLocation] = useAtom(
    userHasAllowedLocationAtom,
  );

  const {
    isOpen: isAgeRestrictionOpen,
    onOpen: openAgeRestriction,
    onClose: closeAgeRestriction,
  } = useDisclosure();
  const {
    isOpen: isDobModalOpen,
    onOpen: openDobModal,
    onClose: closeDobModal,
  } = useDisclosure();
  const {
    isOpen: isSelectCurrencyOpen,
    onOpen: openSelectCurrency,
    onClose: closeSelectCurrency,
  } = useDisclosure();
  const {
    isOpen: isAllowLocationPromptOpen,
    onOpen: openAllowLocationPrompt,
    onClose: closeAllowLocationPrompt,
  } = useDisclosure();
  const {
    isOpen: isAllowLocationManuallyOpen,
    onOpen: openAllowLocationManually,
    onClose: closeAllowLocationManually,
  } = useDisclosure();
  const {
    isOpen: isLocationOutOfBoundsOpen,
    onOpen: openLocationOutOfBounds,
    onClose: closeLocationOutOfBounds,
  } = useDisclosure();

  const handleGetDeviceLocationInfo: HandleGetDeviceLocationInfo = useCallback(
    async (callback) => {
      try {
        const coordinates = (await getDeviceGeoLocation()).coords;
        if (!coordinates) {
          callback?.({ locationStatus: WebApiPermissionStates.DENIED });
          return;
        }

        const { latitude, longitude, accuracy } = coordinates;
        if (accuracy > 500) {
          callback?.({
            locationStatus: WebApiPermissionStates.GRANTED,
            isLocationPrecise: false,
          });
          return;
        }

        const userLocationData = await storeLocationData({
          latitude: latitude.toString(),
          longitude: longitude.toString(),
        });
        if (!userLocationData) {
          callback?.({ locationStatus: WebApiPermissionStates.GRANTED });
          return;
        }

        const { isRestricted, countryCode } = userLocationData;
        if (isRestricted) {
          callback?.({ locationStatus: WebApiPermissionStates.GRANTED });
          setIpLocationCountry(countryCode);
          openLocationOutOfBounds();
          return;
        }

        callback?.({
          locationStatus: WebApiPermissionStates.GRANTED,
          canTopup: true,
        });
        setUserHasAllowedLocation(true);
        !isCustomFlow && navigate(TOPUP_PAGE_ROUTE);
      } catch (error) {
        callback?.({ locationStatus: WebApiPermissionStates.DENIED });
        Logger.log(`Error occurred: ${error}`);
      }
    },
    [
      isCustomFlow,
      navigate,
      openLocationOutOfBounds,
      setUserHasAllowedLocation,
    ],
  );

  const handleDeviceLocationCheck = useCallback(
    async (onSuccess?: FunctionWithVoidReturn) => {
      const geoLocationPermission = await checkGeoLocationPermission();
      if (
        geoLocationPermission !== WebApiPermissionStates.GRANTED &&
        !userHasAllowedLocation
      ) {
        openAllowLocationPrompt();
        return;
      }
      handleGetDeviceLocationInfo(({ locationStatus, canTopup }) => {
        if (locationStatus === WebApiPermissionStates.DENIED) {
          openAllowLocationManually();
        }
        if (canTopup) onSuccess?.();
      });
    },
    [
      handleGetDeviceLocationInfo,
      openAllowLocationManually,
      openAllowLocationPrompt,
      userHasAllowedLocation,
    ],
  );

  /* This is a waterfall-style representation of the topup flow
   *
   * - If they don't have a default currency, ask them to provide their default currency.
   * - If they have a default currency:
   *   - If they don't have a dob, ask them to provide their dob.
   *   - If they have a dob:
   *     - If they are not up to 18, show them the age restriction modal.
   *     - If they are up to 18:
   *       - If user is topping up for the first time, ask permission for their device location and send location data to the backend. If backend says they are good to go, take them to the topup page else show them the "Hyper does not work in your location" modal.
   *       - If they are not topping up for the first time:
   *         - If their IP is in a restricted location, ask permission for their device location and send location data to the backend. If backend says they are good to go, take them to the topup page else show them the "Hyper does not work in your location" modal.
   *         - If their IP is not in a restricted location, take them to the topup page.
   */

  const handleTopup: HandleTopup = useCallback(
    async (params) => {
      const { updatedCurrency, updatedDob, onSuccess } = params ?? {};

      // I'm returning early for all the checks if we don't have the information we need to perform the check,
      // such as when data from the backend has not been received yet. This is to prevent a flawed decision which could trigger the action within a check,
      // if it passes, or trigger the last resort action (to navigate to the top-up page), if none of the checks passes.
      if (!userProfile) return;
      if (!userProfile.currency && !updatedCurrency) {
        openSelectCurrency();
        return;
      }

      if (!userProfile.dob && !updatedDob) {
        openDobModal();
        return;
      }

      const isUpToLegalAge = checkIsLegalAge({
        dob: updatedDob,
        checkDobArgFirst: true,
      });
      if (isUpToLegalAge === undefined) return;
      if (!isUpToLegalAge) {
        openAgeRestriction();
        return;
      }

      if (isFirstTopup === undefined) return;
      if (isFirstTopup || userVerificationData?.isLocationRestricted) {
        handleDeviceLocationCheck(onSuccess);
        return;
      }

      onSuccess?.();
      !isCustomFlow && navigate(TOPUP_PAGE_ROUTE);
    },
    [
      checkIsLegalAge,
      handleDeviceLocationCheck,
      isCustomFlow,
      isFirstTopup,
      navigate,
      openAgeRestriction,
      openDobModal,
      openSelectCurrency,
      userProfile,
      userVerificationData?.isLocationRestricted,
    ],
  );

  const handleTopupRedirect = useCallback(
    async (isFirstTopup?: boolean) => {
      if (
        isCustomFlow ||
        !userProfile ||
        userVerificationIsLoading ||
        isFirstTopup === undefined
      ) {
        return;
      }

      if (
        !userProfile.currency ||
        (!userProfile.dob && !userVerificationData?.dob) ||
        isLegalAge === false ||
        userVerificationData?.isLocationRestricted ||
        (isFirstTopup && !userHasAllowedLocation)
      ) {
        navigate(WALLET_PAGE_ROUTE);
      }
    },
    [
      navigate,
      isCustomFlow,
      userProfile,
      isLegalAge,
      userVerificationData,
      userVerificationIsLoading,
      userHasAllowedLocation,
    ],
  );

  useEffect(() => {
    if (!isLoggedIn) return;

    (async () => {
      const transactionsList = await handleGetMyTransactionHistory();
      const isUserFirstTopup =
        transactionsList && transactionsList.length === 0;

      setIsFirstTopup(isUserFirstTopup);

      if (removeTrailingSlash(location.pathname) === TOPUP_PAGE_ROUTE) {
        handleTopupRedirect(isUserFirstTopup);
      }
    })();
  }, [isLoggedIn, handleTopupRedirect, location.pathname]);

  return {
    isFirstTopup,
    ipLocationCountry,
    handleTopup,
    handleGetDeviceLocationInfo,
    modalsProps: {
      isSelectCurrencyOpen,
      closeSelectCurrency,
      isDobModalOpen,
      closeDobModal,
      isAgeRestrictionOpen,
      closeAgeRestriction,
      isAllowLocationPromptOpen,
      closeAllowLocationPrompt,
      isAllowLocationManuallyOpen,
      openAllowLocationManually,
      closeAllowLocationManually,
      isLocationOutOfBoundsOpen,
      closeLocationOutOfBounds,
    },
  };
}
